import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { compute, sensitivity } from "@/lib/finance";
import { SectionHeader, btn, inputCls } from "@/components/common/Ornament";
import ShortWarning from "@/components/common/ShortWarning";
import FinancialForm from "@/components/financial/FinancialForm";
import KpiGrid from "@/components/financial/KpiGrid";
import { RevenueNoiChart, CumulativeChart, CostDonut } from "@/components/financial/Charts";
import { YearTable, SensitivityTable } from "@/components/financial/Tables";
import ScenarioCompare from "@/components/financial/ScenarioCompare";
import Methodology from "@/components/financial/Methodology";
import CostDetail from "@/components/financial/CostDetail";
import StressTest from "@/components/financial/StressTest";
import { downloadSummaryPdf } from "@/lib/summaryPdf";
import { FileDown } from "lucide-react";

function Toolbar({ presets, active, onPreset, onReset, onSave, canSave, onPdf }) {
  const [name, setName] = useState("");
  const save = () => { onSave(name.trim() || `Scenario ${Date.now() % 1000}`); setName(""); };
  return (
    <div className="paper rounded-lg p-4 flex flex-col xl:flex-row gap-4 xl:items-center xl:justify-between mb-6" data-testid="calc-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-[0.18em] text-[#3E5C4E] font-semibold mr-1">Presets</span>
        {presets.map((p) => (
          <button key={p.id} onClick={() => onPreset(p)} data-testid={`calc-preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`h-9 px-4 rounded-full text-sm border transition-colors ${active === p.name ? "bg-[#0F2E23] text-[#F9F6F0] border-[#0F2E23]" : "bg-white border-[#E2DCD2] hover:border-[#C59B27]"}`}>{p.name}</button>
        ))}
        <button onClick={onReset} className={btn.ghost + " !h-9"} data-testid="calc-reset-btn"><RotateCcw className="h-4 w-4" /> Reset to Baseline</button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Scenario name" className={inputCls + " !h-9 sm:w-48"} data-testid="calc-scenario-name-input" />
        <button onClick={save} disabled={!canSave} className={btn.gold + " !h-9 whitespace-nowrap"} data-testid="calc-save-scenario-btn"><Save className="h-4 w-4" /> Save ({canSave ? "max 3" : "full"})</button>
        <button onClick={onPdf} className={btn.primary + " !h-9 whitespace-nowrap"} data-testid="calc-download-summary-btn"><FileDown className="h-4 w-4" /> Summary PDF</button>
      </div>
    </div>
  );
}

export default function Financials() {
  const { data } = useSWR("/financials");
  const { data: content } = useSWR("/content");
  const [inputs, setInputs] = useState(null);
  const [active, setActive] = useState("Baseline");
  const [saved, setSaved] = useState([]);
  const [stress, setStress] = useState(null);
  const baseR = useMemo(() => (stress ? compute(stress.inputs) : null), [stress]);

  useEffect(() => {
    if (data && !inputs) setInputs({ ramp_y1_pct: 100, ramp_y2_pct: 100, ...data.baseline });
  }, [data, inputs]);

  const r = useMemo(() => (inputs ? compute(inputs) : null), [inputs]);
  const sens = useMemo(() => (inputs ? sensitivity(inputs) : null), [inputs]);
  if (!r) return <div className="h-96 rounded-xl bg-white/60 animate-pulse" />;

  const change = (v) => { setInputs(v); setActive("Custom"); setStress(null); };
  const applyStress = (c) => {
    const base = stress || { inputs, label: active };
    setInputs(c.apply(base.inputs));
    setStress(base);
    setActive(`${base.label} + ${c.label}`);
    toast(`Applied stress case: ${c.label}`);
  };
  const onSave = (name) => {
    setSaved((s) => [...s, { id: Date.now(), name, inputs: { ...inputs }, result: r }]);
    toast.success(`Saved “${name}” for comparison`);
  };

  return (
    <div>
      <SectionHeader eyebrow="Interactive Model" title="Financial Model & Scenario Calculator" testId="financials-header">
        Adjust any assumption to explore what-if scenarios. Your changes are not saved and do not alter the baseline.
      </SectionHeader>
      <ShortWarning testId="calc-short-warning" />
      <Toolbar presets={data.presets} active={active} canSave={saved.length < 3}
        onPreset={(p) => { setInputs({ ...p.inputs }); setActive(p.name); setStress(null); }}
        onReset={() => { setInputs({ ...data.baseline }); setActive("Baseline"); setStress(null); toast("Reset to baseline"); }}
        onSave={onSave}
        onPdf={() => { if (!content) return; downloadSummaryPdf({ content, inputs, scenario: active, stressBase: stress || { inputs, label: "Scenario" } }); toast.success("Summary PDF downloaded"); }} />
      <p className="text-xs text-slate-500 mb-4" data-testid="calc-active-scenario">Showing: <strong className="text-[#0F2E23]">{active}</strong></p>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5 xl:col-span-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:sticky lg:top-6 pr-1">
          <FinancialForm values={inputs} onChange={change} prefix="calc" />
        </div>
        <div className="lg:col-span-7 xl:col-span-8 space-y-6 min-w-0">
          <KpiGrid r={r} />
          <div className="grid gap-6 2xl:grid-cols-2">
            <RevenueNoiChart years={r.years} />
            <CumulativeChart data={r.cumulative} />
          </div>
          <CostDonut data={r.costBreakdown} total={r.totalCost} />
          <CostDetail items={content?.cost_detail} inputs={inputs} />
          <SensitivityTable sens={sens} baseOcc={inputs.occupancy} baseCap={inputs.exit_cap} />
          <StressTest inputs={stress ? stress.inputs : inputs} base={baseR || r} baseLabel={stress ? stress.label : "Current"} onApply={applyStress} />
          <YearTable years={r.years} />
          <ScenarioCompare saved={saved} onRemove={(id) => setSaved((s) => s.filter((x) => x.id !== id))} onLoad={(s) => { setInputs({ ...s.inputs }); setActive(s.name); }} />
          <Methodology />
        </div>
      </div>
    </div>
  );
}
